import { useState, useEffect, Fragment } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { formatNumber } from '../../utils/generalFunctions';

import { buscarDashboard } from '../../actions/dashboard';
import { buscarMembresiasConfiguracion } from '../../actions/configuracion';

const DashboardAdministrador = () => {

    const dispatch =  useDispatch();

    const { detalles } = useSelector(state => state.dashboard);
    const { usuario, empresa } = useSelector(state => state.auth);
    const { listado: catalogoMembresias } = useSelector(state => state.configuracion.membresias);

    const perfilEmpresaCompleto = !!(empresa?.nombre_comercial && empresa?.rfc);

    const [dashboardData, setDashboardData] = useState({
        curso: null, 
        finalizadas: null, 
        archivos: null, 
        membresia: null
    });

    useEffect(() => {
        dispatch(buscarDashboard('administrador'));
        dispatch(buscarMembresiasConfiguracion());
    }, []);

    useEffect(() => {
        if(detalles){
            setDashboardData({
                ...dashboardData, 
                curso: detalles.curso, 
                finalizadas: detalles.finalizadas, 
                archivos: detalles.archivos, 
                membresia: detalles.membresia_actual
            });
        }        
    }, [detalles]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .titulo {
                            color: #111f62;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <div className="container-fluid py-4">

                {/* SALUDO */}
                <div className="mb-5">
                    <h4 className="titulo mb-2">
                        ¡Hola {[usuario.nombre, usuario.apellido_paterno, usuario.apellido_materno].join(' ')}!
                    </h4>
                    <small className="text-muted mb-5">Esta es tu cuenta de Goodlic</small>
                </div>

                {/* BANNER PERFIL EMPRESARIAL INCOMPLETO */}
                {!perfilEmpresaCompleto && (
                    <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 mb-5" role="alert">
                        <div className="d-flex align-items-center gap-2">
                            <i className="fa-solid fa-triangle-exclamation flex-shrink-0"></i>
                            <span>
                                <strong>Perfil empresarial incompleto.</strong> Para poder crear asesorías necesitas completar los datos de tu empresa.
                            </span>
                        </div>
                        <Link className="btn btn-warning btn-sm flex-shrink-0" to="/configuracion/perfil_empresarial">
                            Completar perfil
                        </Link>
                    </div>
                )}

                {/* BANNER CONSTANCIA PENDIENTE */}
                {dashboardData.archivos > 0 && (
                    <div className="alert alert-warning d-flex align-items-center justify-content-between gap-3 mb-5" role="alert">
                        <div className="d-flex align-items-center gap-2">
                            <i className="fa-solid fa-file-circle-exclamation flex-shrink-0"></i>
                            <span>
                                <strong>Tienes {dashboardData.archivos} {dashboardData.archivos === 1 ? 'documento pendiente' : 'documentos pendientes'} de subir.</strong> Goodlic te ha solicitado {dashboardData.archivos === 1 ? 'un documento' : 'documentos'} que aún no has cargado en tu expediente.
                            </span>
                        </div>
                        <Link className="btn btn-warning btn-sm flex-shrink-0" to="/expediente">
                            Ir a expediente
                        </Link>
                    </div>
                )}

                {/* BANNER MEMBRESÍA */}
                {(dashboardData.membresia?.status == 0 || dashboardData.membresia?.status == 1) ? (
                    <div className="card border-0 shadow-sm mb-5 bg-light">
                        <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                            <div>
                                <h5 className="mb-1">
                                    <i className={"fa-solid fa-id-card me-2 text-" + (dashboardData.membresia?.status == 0 ? 'success' : (dashboardData.membresia?.status == 1 ? 'warning' : 'danger'))}></i>
                                    Membresía {dashboardData.membresia?.nombre}
                                    <span className={"badge ms-2 bg-" + (dashboardData.membresia?.status == 0 ? 'success' : (dashboardData.membresia?.status == 1 ? 'warning' : 'danger'))}>{dashboardData.membresia?.status == 0 ? 'Activa' : (dashboardData.membresia?.status == 1 ? 'Vencida' : 'Cancelada')}</span>
                                </h5>
                                <small className={dashboardData.membresia?.cancel_at_period_end ? 'text-warning' : 'text-muted'}>
                                    {dashboardData.membresia?.cancel_at_period_end
                                        ? <><i className="fa-solid fa-ban me-1"></i>Vencimiento (sin renovación): </>
                                        : 'Fecha de renovación: '
                                    }
                                    { dashboardData.membresia?.fecha_renovacion ? new Date(dashboardData.membresia?.fecha_renovacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' }) : '—' }
                                </small>
                                {
                                    dashboardData.membresia?.limite_asesorias != null &&
                                    <small className="text-muted d-block mt-1">
                                        Asesorías este mes: <strong>{dashboardData.membresia.asesorias_restantes}</strong> de {dashboardData.membresia.limite_asesorias} disponibles
                                    </small>
                                }
                            </div>

                            <Link className={"btn mt-3 mt-md-0 btn-" + (dashboardData.membresia?.status == 0 ? 'success' : 'warning')} to="/membresia">
                                Ver mi membresía
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="card border-0 shadow-sm mb-5 bg-light">
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                                <h5 className="mb-1">
                                    <i className="fa-solid fa-circle-exclamation me-2 text-warning"></i>
                                    No tienes una membresía activa
                                </h5>
                                <small className="text-muted">
                                    Contrata una membresía para comenzar a disfrutar de los beneficios
                                </small>
                            </div>

                            <Link className="btn btn-primary" to="/membresia">
                                Contratar membresía
                            </Link>
                        </div>
                    </div>
                )}

                {/* MÉTRICAS + UPGRADE (layout condicional) */}
                {(() => {
                    const membresiaActiva = dashboardData.membresia?.status == 0 || dashboardData.membresia?.status == 1;
                    const total = dashboardData.membresia?.total;
                    const upgrades = (membresiaActiva && total != null)
                        ? catalogoMembresias.filter(m => m.precio > total)
                        : [];

                    const metricCards = (stacked) => (
                        <Fragment>
                            <div className={stacked ? 'col-md-6 mb-3' : 'col-md-4 mb-3'}>
                                <div className="card border-0 shadow-sm h-100 bg-light">
                                    <div className="card-body">
                                        <h6 className="text-muted mb-2">
                                            <i className="fa-solid fa-clock me-2"></i>
                                            Asesorías en curso
                                        </h6>
                                        <h2 className="mb-0">{dashboardData.curso != null ? formatNumber(dashboardData.curso, 0, '', '') : 'Sin definir'}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className={stacked ? 'col-md-6 mb-3' : 'col-md-4 mb-3'}>
                                <div className="card border-0 shadow-sm h-100 bg-light">
                                    <div className="card-body">
                                        <h6 className="text-muted mb-2">
                                            <i className="fa-solid fa-check-circle me-2"></i>
                                            Asesorías finalizadas
                                        </h6>
                                        <h2 className="mb-0">{dashboardData.finalizadas != null ? formatNumber(dashboardData.finalizadas, 0, '', '') : 'Sin definir'}</h2>
                                    </div>
                                </div>
                            </div>
                            <div className={stacked ? 'col-md-6' : 'col-md-4 mb-3'}>
                                <div className="card border-0 shadow-sm h-100 bg-light">
                                    <div className="card-body">
                                        <h6 className="text-muted mb-2">
                                            <i className="fa-solid fa-file me-2"></i>
                                            Archivos pendientes
                                        </h6>
                                        <h2 className="mb-0">{dashboardData.archivos != null ? formatNumber(dashboardData.archivos, 0, '', '') : 'Sin definir'}</h2>
                                    </div>
                                </div>
                            </div>
                        </Fragment>
                    );

                    if (upgrades.length > 0) {
                        return (
                            <div className="row mb-5">
                                <div className="col-md-6 row mt-3">
                                    {metricCards(true)}
                                </div>
                                <div className="col-md-6 mt-3">
                                    <h6 className="titulo mb-1">
                                        <i className="fa-solid fa-arrow-trend-up me-2"></i>
                                        Mejora tu plan
                                    </h6>
                                    <p className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
                                        Con un plan superior accedes a más beneficios y asesorías.
                                    </p>
                                    {upgrades.map(plan => (
                                        <div className="card border-0 shadow-sm mb-3" key={plan._id}>
                                            <div className="card-header border-0 py-3 d-flex justify-content-between align-items-center" style={{ background: 'linear-gradient(135deg, #0d6958 0%, #0CB394 100%)', borderRadius: '8px 8px 0 0' }}>
                                                <div>
                                                    <h6 className="mb-0 text-white">{plan.nombre}</h6>
                                                    <small className="text-white opacity-75">
                                                        {plan.precio === 0 ? 'Gratuita' : formatNumber(plan.precio, 2, '$ ', '') + ' / año · IVA inc.'}
                                                    </small>
                                                </div>
                                                <Link className="btn btn-light btn-sm" to="/membresia">
                                                    Contratar
                                                </Link>
                                            </div>
                                            <div className="card-body py-2">
                                                <div className="row">
                                                    {plan.beneficios?.map((b, j) => (
                                                        <div key={j} className="col-md-6 d-flex align-items-start gap-2 mb-1">
                                                            <i className="fa-solid fa-check text-success mt-1" style={{ fontSize: '0.7rem', flexShrink: 0 }}></i>
                                                            <small>{b}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="row mb-5">
                            {metricCards(false)}
                        </div>
                    );
                })()}

                {/* BANNER SIN ASESORÍAS */}
                {dashboardData.membresia?.status == 0 && dashboardData.curso == 0 && (
                    <div className="card border-0 shadow-sm bg-light mb-5">
                        <div className="card-body d-flex justify-content-between align-items-center">
                            <div>
                                <h6 className="mb-1">
                                    <i className="fa-solid fa-calendar-plus me-2 text-primary"></i>
                                    No tienes asesorías en curso por ahora
                                </h6>
                                <small className="text-muted">
                                    {perfilEmpresaCompleto
                                        ? 'Puedes iniciar una nueva asesoría cuando lo desees'
                                        : 'Completa tu perfil empresarial para poder crear asesorías'}
                                </small>
                            </div>
                            {perfilEmpresaCompleto
                                ? <Link className="btn btn-primary" to="/asesorias/nueva">Nueva asesoría</Link>
                                : <Link className="btn btn-warning" to="/configuracion/perfil_empresarial">Completar perfil</Link>
                            }
                        </div>
                    </div>
                )}

                {/* ACCIONES RÁPIDAS */}
                {
                    dashboardData.membresia?.status == 0 &&
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                {perfilEmpresaCompleto
                                    ? <Link className="btn btn-primary w-100 py-3" to="/asesorias/nueva">
                                        <i className="fa-solid fa-plus me-2"></i> Nueva asesoría
                                      </Link>
                                    : <button
                                        type="button"
                                        className="btn btn-primary w-100 py-3"
                                        disabled
                                        title="Completa tu perfil empresarial para crear asesorías"
                                      >
                                        <i className="fa-solid fa-plus me-2"></i> Nueva asesoría
                                      </button>
                                }
                            </div>

                            <div className="col-md-4 mb-3">
                                <Link className="btn btn-outline-secondary w-100 py-3" to="/asesorias">
                                    <i className="fa-solid fa-list me-2"></i> Ver asesorías
                                </Link>
                            </div>

                            <div className="col-md-4 mb-3">
                                <Link className="btn btn-outline-secondary w-100 py-3" to="/membresia">
                                    <i className="fa-solid fa-receipt me-2"></i> Historial de membresías
                                </Link>
                            </div>
                        </div>
                }
            </div>
        </Fragment>
    )
}

export default DashboardAdministrador;